import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../core/services/auth/auth.service';
import { getMockTestCaseComments } from '../../data/mock-testhub-data';
import { TestCaseComment, TestCaseCommentInsertRequest } from '../../models/test-case-comment';
import { UtilityService } from '../utility/utility.service';

/** Comments on a test case (PBI 57768), nested under the test case in the API. */
@Injectable({
  providedIn: 'root',
})
export class TestCaseCommentService {
  private utilityService = inject(UtilityService);
  private auth = inject(AuthService);

  testCaseEndpoint = `${environment.testHubApiUrl}/v1/TestCase`;

  /** Mock mode only: comments posted this session, so a thread survives stepper moves. */
  private mockPosted = new Map<number, TestCaseComment[]>();

  /** Every comment on the test case, oldest first. Empty when there are none. */
  getByTestCaseId(testCaseId: number): Observable<TestCaseComment[]> {
    if (environment.useMockData) {
      return of([
        ...getMockTestCaseComments(testCaseId),
        ...(this.mockPosted.get(testCaseId) ?? []),
      ]);
    }
    return this.utilityService.getRequest<TestCaseComment[]>(
      `${this.testCaseEndpoint}/${testCaseId}/Comment`,
    );
  }

  /** Post a comment; resolves to the saved comment as the API returns it. */
  create(testCaseId: number, request: TestCaseCommentInsertRequest): Observable<TestCaseComment> {
    if (environment.useMockData) {
      const user = this.auth.userSig();
      const comment: TestCaseComment = {
        testCaseCommentId: Date.now(),
        testCaseId,
        userId: request.userId,
        userName: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim(),
        userRole: user?.role ?? 'Tester',
        body: request.body.trim(),
        isDeleted: false,
        createdOnUtc: new Date().toISOString(),
        modifiedOnUtc: null,
      };
      this.mockPosted.set(testCaseId, [...(this.mockPosted.get(testCaseId) ?? []), comment]);
      return of(comment);
    }
    return this.utilityService.postRequest<TestCaseComment>(
      `${this.testCaseEndpoint}/${testCaseId}/Comment`,
      request,
    );
  }
}
